import React from 'react';

import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import HtmlText from '../HtmlText';
import {
  buildRatingValue,
  type RatingComponentConfig,
  type RatingState,
} from './ratingUtils';

interface InlineFeedbackProps {
  config: RatingComponentConfig;
  locked: boolean;
  ratingState: RatingState;
  setValue: (componentId: string, value: unknown) => void;
}

const InlineFeedback: React.FC<InlineFeedbackProps> = ({
  config,
  locked,
  ratingState,
  setValue,
}) => {
  if (config.feedbackMode !== 'inline') return null;

  const updateFeedback = (feedback: RatingState['feedback']) => {
    if (ratingState.score === null) {
      setValue(config.id, { ...ratingState, feedback, submitted: false });
      return;
    }

    setValue(config.id, buildRatingValue(config, { ...ratingState, feedback }, ratingState.score, false));
  };

  const toggleTag = (tag: string) => {
    if (locked) return;

    updateFeedback({
      ...ratingState.feedback,
      tags: ratingState.feedback.tags.includes(tag)
        ? ratingState.feedback.tags.filter((selectedTag) => selectedTag !== tag)
        : [...ratingState.feedback.tags, tag],
    });
  };

  return (
    <Card
      sx={{
        bgcolor: 'var(--cx-brand-bg)',
        border: '1px solid var(--cx-brand-border)',
        boxShadow: 'none',
        p: 1.5,
      }}
    >
      <Stack spacing={1.25}>
        <HtmlText component="div" html={config.feedbackPrompt ?? 'Add optional context'} variant="subtitle2" />
        {config.feedbackTags?.length ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {config.feedbackTags.map((tag) => {
              const selected = ratingState.feedback.tags.includes(tag);

              return (
                <Chip
                  disabled={locked}
                  key={tag}
                  label={tag}
                  onClick={() => toggleTag(tag)}
                  sx={{
                    bgcolor: selected ? 'var(--cx-brand-button-selected-bg)' : 'var(--cx-brand-button-bg)',
                    border: '1px solid',
                    borderColor: selected
                      ? 'var(--cx-brand-button-selected-border)'
                      : 'var(--cx-brand-button-border)',
                    color: selected ? 'var(--cx-brand-button-selected-text)' : 'var(--cx-brand-button-text)',
                    fontWeight: 700,
                    '&:hover': {
                      bgcolor: selected
                        ? 'var(--cx-brand-button-selected-bg)'
                        : 'var(--cx-brand-button-hover-bg)',
                    },
                  }}
                />
              );
            })}
          </Stack>
        ) : null}
        <TextField
          disabled={locked}
          fullWidth
          multiline
          minRows={3}
          onChange={(event) => updateFeedback({ ...ratingState.feedback, notes: event.target.value })}
          placeholder={config.feedbackPlaceholder ?? 'Tell us what stood out or what we should fix.'}
          sx={{
            '& .MuiInputBase-root': {
              bgcolor: 'var(--cx-brand-surface)',
              color: 'var(--cx-brand-text)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--cx-brand-border)',
            },
          }}
          value={ratingState.feedback.notes}
        />
      </Stack>
    </Card>
  );
};

export default InlineFeedback;
